import { createContext, useContext, useState, useCallback } from "react";
import { translations, LANGUAGES } from "@/i18n/translations";

const LanguageContext = createContext(null);

const getInitial = () => {
  const saved =
    typeof window !== "undefined" && localStorage.getItem("gd_lang");
  if (saved && translations[saved]) return saved;
  const nav =
    typeof navigator !== "undefined" && navigator.language
      ? navigator.language.slice(0, 2)
      : "it";
  return translations[nav] ? nav : "it";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitial);

  const setLang = useCallback((code) => {
    if (!translations[code]) return;
    localStorage.setItem("gd_lang", code);
    setLangState(code);
  }, []);

  const t = useCallback((key) => translations[lang][key] ?? key, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};

export const LanguageSwitcher = ({ className = "" }) => {
  const { lang, setLang, languages } = useLang();
  return (
    <div
      data-testid="language-switcher"
      className={`inline-flex items-center border border-[#d4af37]/40 rounded-full overflow-hidden text-[11px] font-semibold ${className}`}
    >
      {languages.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-3 py-1.5 tracking-wider transition-colors ${
            lang === l.code
              ? "bg-[#d4af37] text-[#060a14]"
              : "text-[#d4af37]/80 hover:text-[#d4af37]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};
